import { FC } from 'react';
import { useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import formatCurrency from '@/shared/utils/formatCurrency';

import useFetchData from './hooks/useFetchData';

const OrderInvoice: FC = () => {
  const orderId = useParams().id || '';

  const { order, profileTemplates, shippingProfile } = useFetchData({
    orderId,
  });

  return (
    <Stack
      sx={{
        height: '100vh',
        maxWidth: 720,
        width: '100%',
        margin: '0 auto',
        display: 'grid',
        gridTemplateRows: '1fr auto',
      }}
    >
      <Stack sx={{ flex: 1 }}>
        <Box
          component='img'
          sx={{
            width: '100%',
            height: 140,
            objectFit: 'cover',
            objectPosition: '50% 50%',
            mb: 3,
          }}
          alt='invoice image'
          src='/images/img.jpeg'
        />

        <Stack
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            columnGap: 2,
            mb: 3,
          }}
        >
          <Stack>
            <Typography variant='subtitle2' textTransform='uppercase'>
              billing address
            </Typography>
            <Typography>{order?.billing_name}</Typography>
            <Typography>{order?.billing_address}</Typography>
            <Typography>{order?.billing_address2}</Typography>
            <Typography>{`${order?.billing_city} ${order?.billing_state} ${order?.billing_postcode} - ${order?.billing_country}`}</Typography>
            <Typography>{order?.billing_phone}</Typography>
            <Typography>{order?.email_address}</Typography>
          </Stack>
          <Stack>
            <Typography variant='subtitle2' textTransform='uppercase'>
              shipping address
            </Typography>
            <Typography>{order?.delivery_name}</Typography>
            <Typography>{order?.delivery_address}</Typography>
            <Typography>{order?.delivery_address2}</Typography>
            <Typography>{`${order?.delivery_city} ${order?.delivery_state} ${order?.delivery_postcode} - ${order?.delivery_country}`}</Typography>
            <Typography>{order?.delivery_phone}</Typography>
            <Typography>{order?.email_address}</Typography>
          </Stack>
        </Stack>

        {profileTemplates && (
          <Stack sx={{ mb: 3 }}>
            <Typography variant='subtitle2' textTransform='uppercase'>
              shipping method
            </Typography>
            <Typography>{shippingProfile?.display_name}</Typography>
          </Stack>
        )}

        <Stack
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            columnGap: 2,
            mb: 3,
          }}
        >
          <Stack>
            <Typography variant='subtitle2' textTransform='uppercase'>
              payment type
            </Typography>

            {order?.payments.map((payment) => (
              <Typography key={payment.in_transaction_id}>
                {order.payment_method ? `${order.payment_method}: ` : ''}
                {payment.payment_method}
              </Typography>
            ))}
          </Stack>
          <Stack>
            <Typography variant='subtitle2' textTransform='uppercase'>
              total items
            </Typography>
            <Typography>
              {order?.products.reduce(
                (acc, curr) => acc + parseInt(curr.product_qty),
                0,
              )}
            </Typography>
          </Stack>
        </Stack>

        <Stack sx={{ mb: 4 }}>
          <Stack
            sx={{
              display: 'grid',
              gridTemplateColumns: '100px 300px 100px 50px 50px',
              columnGap: 2,
            }}
          >
            <Typography variant='subtitle2' textTransform='uppercase'>
              sku
            </Typography>
            <Typography variant='subtitle2' textTransform='uppercase'>
              description and options
            </Typography>
            <Typography variant='subtitle2' textTransform='uppercase'>
              price
            </Typography>
            <Typography variant='subtitle2' textTransform='uppercase'>
              qty
            </Typography>
            <Typography variant='subtitle2' textTransform='uppercase'>
              total
            </Typography>
          </Stack>
          {order?.products.map((product) => {
            return (
              <Stack
                key={product.variant_id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '100px 300px 100px 50px 50px',
                  columnGap: 2,
                }}
              >
                <Typography>{product.sku || '-'}</Typography>
                <Typography>{product.product_name}</Typography>
                <Typography>
                  {formatCurrency(
                    parseFloat(product.pre_tax_price) /
                      parseInt(product.product_qty),
                  )}
                </Typography>
                <Typography>{product.product_qty}</Typography>
                <Typography>
                  {formatCurrency(parseFloat(product.pre_tax_price))}
                </Typography>
              </Stack>
            );
          })}
        </Stack>

        <Stack alignItems='flex-end'>
          <Typography variant='subtitle2'>
            {`Sub Total: ${formatCurrency(parseFloat(order?.sub_total || ''))}`}
          </Typography>
          <Typography variant='subtitle2'>
            Discount: {formatCurrency(parseFloat(order?.discount || '0'))}
          </Typography>
          <Typography variant='subtitle2'>
            {`Tax: ${formatCurrency(parseFloat(order?.tax || ''))}`}
          </Typography>
          <Typography variant='subtitle2'>
            {`Shipping: ${formatCurrency(parseFloat(order?.shipping_price || ''))}`}
          </Typography>
          <Typography variant='subtitle2'>
            {`Order Total: ${formatCurrency(parseFloat(order?.order_total || ''))}`}
          </Typography>
        </Stack>
      </Stack>

      <Stack
        sx={{
          maxWidth: 465,
          margin: '0 auto',
        }}
      >
        <Typography textAlign='center'>
          Feathered Addictions, 1680 Broadway Street 107, Port Coquitlam, BC,
          CA, V3C 2M8, (778) 908-1705 info@featheredaddictions.com
        </Typography>
      </Stack>
    </Stack>
  );
};

export default OrderInvoice;
